import React, { useState, useEffect, useMemo } from "react"
import classnames from "classnames"
import JSZip from "jszip"
import * as uuid from "uuid"
import * as httpx from "httpx"
import * as authzc from "authzcached"
import * as layouts from "layouts"
import * as inputs from "inputs"
import * as sessions from "sessions"
import * as brands from "brands"
import * as api from "brandguard/api"
import * as predictions from "brandguard/predictions"
import * as bgimages from "brandguard/display/brandguard/images"
import * as bglayouts from "brandguard/components/layouts"
import * as bgapprovals from "brandguard/display/approval"
import { UploadSimple } from "brandguard/components"
import { uploads } from "brandguard/display/training"
import * as authz from "authz"
import * as nav from "brandguard/components/navigation"
import * as icons from "icons"
import * as media from "media"
import * as cardstatuspanel from "brandguard/display/training/display/status"
import * as caching from "brandguard/display/training/cache"
import { CancellablePromise } from "real-cancellable-promise"
import { Details } from "."

export default function Display(props: React.PropsWithChildren<unknown>): JSX.Element {
	const metaauthz = authzc.useCache((cached) => cached.meta)
	const permission = metaauthz.current
	const bearertoken = sessions.useToken()
	const brand = brands.caching.useCached()
	const cachekey = `imguploads.${brand.id}`

	const [loading, setLoading] = useState(true)
	const [selectedItem, setSelectedItem] = useState(undefined as api.ImageSearchResponseItem | undefined)
	const [checkedUploadItems, setCheckedUploadItems] = useState([] as api.ImageSearchResponseItem[])
	const [onBrandUploadFocused, setOnbrandUploadFocused] = useState(false)
	const [offBrandUploadFocused, setOffbrandUploadFocused] = useState(false)
	const [filterFocused, setFilterFocused] = useState(false)

	const _searchReq = api.images.uploads.searches.zero({
		offset: 0n,
		limit: 20n,
		brand_id: brand.id,
	})

	const [searchReq, setSearchReq] = useState(_searchReq)
	const [cursorKey, setCursorKey] = useState(uuid.v4())

	const [images, setImages] = useState({
		items: [],
		next: searchReq,
	} as api.ImageSearchResponse)

	useEffect(() => {
		if (searchReq.brand_id === uuid.NIL) return
		if (!loading) setLoading(true)
		const retry = httpx.autoretry()
		const p = retry
			.wrap(() => api.images.training.search(searchReq, searchReq.brand_id, bearertoken))
			.then(setImages)
			.catch(httpx.errors.cancellation(() => undefined))
			.catch(console.error)
			.finally(() => setLoading(false))
		return p.cancel
	}, [searchReq])

	const onImageUpdate = (item: api.ImageSearchResponseItem): void => {
		setImages((prevState) => ({
			...prevState,
			items: prevState.items.map((i) => (i.media?.id === item.media?.id ? item : i)),
		}))
	}

	const setUnion = (upd: api.ImageSearchResponseItem): void => {
		setImages((prevState) => ({
			...prevState,
			items: [upd, ...prevState.items.filter((o) => o.media?.id !== upd.media?.id)],
		}))
	}

	const _allselected = images.items.length > 0 && checkedUploadItems.length === images.items.length

	const handleSelectAll = (): void => {
		if (_allselected) {
			setCheckedUploadItems([])
			return
		}
		setCheckedUploadItems(images.items)
	}

	const appchecked = searchReq.observation.length === 1 && searchReq.observation[0] === api.Prediction.APPROVED
	const rejchecked = searchReq.observation.length === 1 && searchReq.observation[0] === api.Prediction.REJECTED
	const fcnt = bgapprovals.CalculateFilterSCount(_searchReq, searchReq)

	const MemoOnBrandUpload = useMemo(() => {
		return function MemodUpload(props: inputs.uploads.api.Props) {
			return (
				<bgimages.uploads.default
					background={layouts.theme.colors.blue.blue}
					observation={api.Prediction.APPROVED}
					{...props}
				/>
			)
		}
	}, [])

	const MemoOffBrandUpload = useMemo(() => {
		return function MemodUpload(props: inputs.uploads.api.Props) {
			return (
				<bgimages.uploads.default
					background={layouts.theme.colors.red.cinnabar}
					observation={api.Prediction.REJECTED}
					{...props}
				/>
			)
		}
	}, [])

	const SelectedDisplay = () => {
		if (!selectedItem) return <></>
		return (
			<layouts.containers.absolute zIndex="1">
				<Details
					item={selectedItem}
					canAction={permission.brandguard_upload}
					deleteAction={api.images.training.patch}
					onDelete={(results) => {
						setSelectedItem(undefined)
						setImages((prevState) => ({
							...prevState,
							items: prevState.items.filter((i) => !results.map((el) => el.media!.id).includes(i.media!.id)),
						}))
					}}
					onClose={() => setSelectedItem(undefined)}
					status={
						<cardstatuspanel.default
							prediction={selectedItem.event?.observation}
							borderRadius="10px"
							minHeight="35px"
						/>
					}
				/>
			</layouts.containers.absolute>
		)
	}

	return (
		<layouts.containers.flex flexDirection="column-reverse" overflow="hidden" data-testid="img-training-upload-display">
			<layouts.pagination.Cursor
				key={cursorKey}
				my="auto"
				justifyContent="center"
				current={searchReq.offset}
				advance={Number(images.next!.offset) === -1 ? undefined : images.next?.offset}
				onChange={(next) => {
					setSearchReq({
						...searchReq,
						offset: next,
					})
				}}
			/>
			<layouts.loading.screen
				className="brandguard-working-area"
				loading={loading}
				flex="1"
				overflow="hidden"
				icon={<></>}
				flexDirection="row"
			>
				<layouts.containers.flex
					className="center-panel"
					flex="1"
					flexDirection="column"
					position="relative"
					alignItems="center"
					justifyContent="center"
				>
					<SelectedDisplay />
					<layouts.containers.flex flexDirection="column" flex="1" minHeight="100px" width="100%">
						<bglayouts.OverlaysContainer overflow="auto" background={layouts.theme.backgrounds.whitealpha80}>
							<layouts.containers.ResponsiveGrid gap={20} p="25px">
								{images.items.map((item) => {
									return (
										<bgapprovals.images.CardDisplay
											key={item.media?.id}
											item={item}
											active={selectedItem && item?.media?.id === selectedItem.media?.id}
											onCardClick={() => {
												setSelectedItem(item)
											}}
											onChange={onImageUpdate}
											onChecked={(checked) =>
												setCheckedUploadItems((prevState) =>
													checked ? [...prevState, item] : prevState.filter((i) => i.media?.id !== item.media?.id),
												)
											}
											panel={
												<cardstatuspanel.default
													prediction={item.event?.observation}
													borderRadius="0 0 10px 10px"
													height="35px"
												/>
											}
											find={api.images.uploads.find}
											checked={checkedUploadItems.some((i) => i.media?.id === item.media?.id)}
										/>
									)
								})}
							</layouts.containers.ResponsiveGrid>
							<layouts.loading.pending loading={loading || images.next?.offset === searchReq.offset}>
								{images.items.length === 0 && <bgapprovals.layouts.ZeroState />}
							</layouts.loading.pending>
						</bglayouts.OverlaysContainer>
					</layouts.containers.flex>
				</layouts.containers.flex>
			</layouts.loading.screen>
			<layouts.containers.flex flexDirection="row" className={classnames(bgapprovals.layouts.styledactions)}>
				<layouts.containers.flex flexDirection="column" mb="5px" zIndex="0" position="relative" flex="1" mr="10px">
					<layouts.buttons.outline
						width="220px"
						height="40px"
						borderRadius="1px"
						onClick={() => {
							if (filterFocused) setFilterFocused(false)
							if (offBrandUploadFocused) setOffbrandUploadFocused(false)
							setOnbrandUploadFocused(!onBrandUploadFocused)
						}}
					>
						{onBrandUploadFocused ? (
							<icons.Plus height="20px" width="20px" stroke={layouts.theme.colors.blue.blue} />
						) : (
							<icons.Plus height="20px" width="20px" stroke={layouts.theme.colors.blue.blue} />
						)}
						<layouts.Span px="10px">Upload On-Brand Assets</layouts.Span>
					</layouts.buttons.outline>
					<layouts.containers.flex
						display={onBrandUploadFocused ? "flex" : "none"}
						position="absolute"
						top="100%"
						pt="5px"
					>
						<UploadSimple
							className={classnames(nav.layouts.slideDownAnimation)}
							title="Upload On-Brand Assets"
							isProcessing={(i: api.ImageSearchResponseItem) => predictions.processing(i.event?.approved)}
							onChange={(img) => {
								caching.uploads.img.remove(cachekey, [img])
								setUnion(img)
							}}
							resume={() => {
								return caching.uploads.img.getasync(cachekey).then((imgs) => {
									return imgs
										.filter((img) => img.event?.observation === api.Prediction.APPROVED)
										.map(
											(i) =>
												({
													id: uuid.v4(),
													display: i.media?.description,
													background: layouts.theme.colors.blue.blue,
													data: i,
												} as inputs.uploads.api.uploaded<api.ImageSearchResponseItem>),
										)
								})
							}}
							refresh={(data) => {
								return api.images.uploads
									.find(data.media?.id!, brand.id, bearertoken)
									.catch(
										httpx.errors.notFound((cause) => {
											console.debug("unable to find training on-brand images", cause)
											return api.images.training.find(data.media?.id!, brand.id, bearertoken)
										}),
									)
									.then((resp) => {
										if (resp.event?.approved === api.Prediction.ERROR) {
											caching.uploads.img.remove(cachekey, [data])
											return CancellablePromise.reject(resp)
										} else {
											return CancellablePromise.resolve(resp)
										}
									})
							}}
							upload={(stats, content) => {
								const delay = uploads.next(cachekey)
								const data = new FormData()
								data.append("observation", api.Prediction.APPROVED.toString())
								data.append("delay", delay.toMillis().toFixed(0))
								return api.images.training.upload(data, content, brand.id, authzc.bearer(metaauthz)).then((resp) => {
									caching.uploads.img.add(cachekey, [resp])
									return resp
								})
							}}
							DropWell={MemoOnBrandUpload}
							onClose={() => setOnbrandUploadFocused(false)}
						/>
					</layouts.containers.flex>
				</layouts.containers.flex>
				<layouts.containers.flex flexDirection="column" mb="5px" zIndex="0" position="relative" flex="1" mr="10px">
					<layouts.buttons.outlinered
						width="230px"
						height="40px"
						borderRadius="1px"
						onClick={() => {
							if (filterFocused) setFilterFocused(false)
							if (onBrandUploadFocused) setOnbrandUploadFocused(false)
							setOffbrandUploadFocused(!offBrandUploadFocused)
						}}
					>
						{offBrandUploadFocused ? (
							<icons.Plus height="20px" width="20px" stroke={layouts.theme.colors.red.cinnabar} />
						) : (
							<icons.Plus height="20px" width="20px" stroke={layouts.theme.colors.red.cinnabar} />
						)}
						<layouts.Span px="10px">Upload Off-Brand Assets</layouts.Span>
					</layouts.buttons.outlinered>
					<layouts.containers.flex
						display={offBrandUploadFocused ? "flex" : "none"}
						position="absolute"
						top="100%"
						pt="5px"
					>
						<UploadSimple
							className={classnames(nav.layouts.slideDownAnimation)}
							title="Upload Off-Brand Assets"
							isProcessing={(i: api.ImageSearchResponseItem) => predictions.processing(i.event?.approved)}
							onChange={(img) => {
								caching.uploads.img.remove(cachekey, [img])
								setUnion(img)
							}}
							resume={() => {
								return caching.uploads.img.getasync(cachekey).then((imgs) => {
									return imgs
										.filter((img) => img.event?.observation === api.Prediction.REJECTED)
										.map(
											(i) =>
												({
													id: uuid.v4(),
													display: i.media?.description,
													background: layouts.theme.colors.red.cinnabar,
													data: i,
												} as inputs.uploads.api.uploaded<api.ImageSearchResponseItem>),
										)
								})
							}}
							refresh={(data) => {
								return api.images.uploads
									.find(data.media?.id!, brand.id, bearertoken)
									.catch(
										httpx.errors.notFound((cause) => {
											console.debug("unable to find training off-brand images", cause)
											return api.images.training.find(data.media?.id!, brand.id, bearertoken)
										}),
									)
									.then((resp) => {
										if (resp.event?.approved === api.Prediction.ERROR) {
											caching.uploads.img.remove(cachekey, [data])
											return CancellablePromise.reject(resp)
										} else {
											return CancellablePromise.resolve(resp)
										}
									})
							}}
							upload={(stats, content) => {
								const data = new FormData()
								data.append("observation", api.Prediction.REJECTED.toString())
								return api.images.training.upload(data, content, brand.id, authzc.bearer(metaauthz)).then((resp) => {
									caching.uploads.img.add(cachekey, [resp])
									return resp
								})
							}}
							DropWell={MemoOffBrandUpload}
							onClose={() => setOffbrandUploadFocused(false)}
						/>
					</layouts.containers.flex>
				</layouts.containers.flex>
				<layouts.containers.flex flexDirection="column" mb="5px" zIndex="0" position="relative">
					<bgapprovals.layouts.FilterButton
						width="145px"
						height="40px"
						borderRadius="4px"
						onClick={() => {
							if (onBrandUploadFocused) setOnbrandUploadFocused(false)
							if (offBrandUploadFocused) setOffbrandUploadFocused(false)
							setFilterFocused(!filterFocused)
						}}
						className={fcnt > 0 ? "active" : ""}
					>
						<layouts.containers.flex flexDirection="row" justifyContent="center">
							<layouts.containers.box>
								<icons.bgapproval.filter />
							</layouts.containers.box>
							<layouts.containers.box px="10px">Filter{fcnt > 0 ? `/${fcnt}` : ""}</layouts.containers.box>
							<layouts.containers.box>
								{filterFocused ? <icons.bgapproval.arrowDown /> : <icons.bgapproval.arrowDown />}
							</layouts.containers.box>
						</layouts.containers.flex>
					</bgapprovals.layouts.FilterButton>
					<layouts.containers.flex display={filterFocused ? "flex" : "none"} position="absolute" top="100%" pt="5px">
						<bgapprovals.FilterDisplay
							onFilterClear={() => {
								setSearchReq(_searchReq)
								setCursorKey(uuid.v4())
							}}
							_searchReq={_searchReq}
							onFilterChange={(r) => {
								setSearchReq({ ...searchReq, ...r, ...{ offset: 0n } })
								setCursorKey(uuid.v4())
							}}
						>
							<bgapprovals.Checkbox
								description="On-Brand Assets"
								checked={appchecked}
								onChange={() => {
									setSearchReq((prevState) => ({
										...prevState,
										observation: appchecked ? _searchReq.observation : [api.Prediction.APPROVED],
										offset: 0n,
									}))
									setCursorKey(uuid.v4())
								}}
							/>
							<bgapprovals.Checkbox
								description="Off-Brand Assets"
								checked={rejchecked}
								onChange={() => {
									setSearchReq((prevState) => ({
										...prevState,
										observation: rejchecked ? _searchReq.observation : [api.Prediction.REJECTED],
										offset: 0n,
									}))
									setCursorKey(uuid.v4())
								}}
							/>
						</bgapprovals.FilterDisplay>
					</layouts.containers.flex>
				</layouts.containers.flex>
				<layouts.containers.flex className="actions" justifyContent="flex-end" flex="10">
					<bgapprovals.layouts.SelectAll
						disabled={images.items.length === 0}
						cursor="pointer"
						className="select-all-action"
						onClick={handleSelectAll}
					>
						{_allselected ? "Unselect All" : "Select All"}
					</bgapprovals.layouts.SelectAll>
					<authz.Protected
						enabled={permission.brandguard_upload && checkedUploadItems.length > 0}
						rejected={
							<>
								<layouts.containers.flex className="download-action">
									<icons.brandguard.download />
								</layouts.containers.flex>
								<layouts.containers.flex className="remove-action">
									<icons.brandguard.remove />
								</layouts.containers.flex>
							</>
						}
					>
						<layouts.containers.flex
							className="download-action"
							cursor="pointer"
							onClick={() => {
								const zip = new JSZip()
								const promises: Promise<api.ImagePatchRequest | JSZip>[] = []
								checkedUploadItems.forEach((i) => {
									promises.push(
										media.cache.current(metaauthz.current.id, i.media!.md5, bearertoken).then((url) => {
											return fetch(url)
												.then((response) => {
													return response.arrayBuffer()
												})
												.then((imageBuffer) => {
													const imageName = media.downloads.filename(i.media!.md5, i.media!.mimetype)
													return zip.file(imageName, imageBuffer)
												})
										}),
									)
								})
								Promise.all(promises).then(() => {
									zip.generateAsync({ type: "blob" }).then((content) => {
										const downloadLink = document.createElement("a")
										downloadLink.href = URL.createObjectURL(content)
										downloadLink.download = "brandguard-assets.zip"
										downloadLink.click()
									})
								})
							}}
						>
							<icons.brandguard.download />
						</layouts.containers.flex>
						<layouts.containers.flex
							className="remove-action"
							cursor="pointer"
							onClick={() => {
								const promises: Promise<api.ImagePatchRequest>[] = []
								checkedUploadItems.forEach((i) => {
									i.media!.tombstoned_at = new Date().toISOString()
									promises.push(api.images.training.patch(i.media!.brand_id, i.media!.id, i, authzc.bearer(metaauthz)))
								})
								Promise.all(promises)
									.then((results) => {
										setCheckedUploadItems((prevState) =>
											prevState.filter((i) => !results.map((el) => el.media!.id).includes(i.media!.id)),
										)
										setImages((prevState) => ({
											...prevState,
											items: prevState.items.filter((i) => !results.map((el) => el.media!.id).includes(i.media!.id)),
										}))
									})
									.catch((cause) => console.error("unable to display checked training images", cause))
									.finally(() => {
										setSearchReq((prevState) => ({ ...prevState, offset: 0n }))
										setCursorKey(uuid.v4())
									})
							}}
						>
							<icons.brandguard.remove />
						</layouts.containers.flex>
					</authz.Protected>
				</layouts.containers.flex>
			</layouts.containers.flex>
		</layouts.containers.flex>
	)
}
